import { safeShortcuts as safe } from '@myua/api';

// Client for the asset_lab AI proxy (`/api/asset-lab/ai/*`, MYUA-16447).
//
// The browser no longer talks to Gemini/OpenAI. A user registers a provider key
// once (POST /api-keys/ — the only place the raw secret is ever sent), the
// backend stores it encrypted, and every generation afterwards references it by
// `api_key_id`. The *provider* is derived server-side from that key, which is
// why the generation payload carries no provider field.
//
// Everything here uses `safeShortcuts` (never throws; returns
// `{ success, data }`, with `data: TAPIError` on failure). Callers must run the
// failure `data` through describeAiError (aiErrors.ts) — reading `data.error` is
// wrong: for 422 / 429 the backend's `detail` is an array / object and
// @myua/api's `error` field ends up non-string despite its `z.string()` type.

const AI_API = '/api/asset-lab/ai';

// Where a user gets a key for each provider — linked from the key form.
export const GEMINI_KEYS_URL = 'https://aistudio.google.com/app/apikey';
export const OPENAI_KEYS_URL = 'https://platform.openai.com/api-keys';

// ─── Formatting ──────────────────────────────────────────────────────
// Money as the user reads it on an invoice: "$12.50". Every budget figure in the
// module (key list, model picker headers, the 429 message) goes through this one
// helper. Undefined/NaN degrade to "$0.00" rather than "$NaN".
export const usd = (value: number | null | undefined): string => {
  const n = typeof value === 'number' && Number.isFinite(value) ? value : 0;
  return `$${n.toFixed(2)}`;
};

// ─── Keys ────────────────────────────────────────────────────────────
// The backend's provider enum — the spelling it stores and validates keys
// against, not a display name. 'openai' covers the image endpoint (gpt-image-1);
// a key registered under any other spelling ('image_gpt', which this module used
// to send) is rejected on POST /ai/api-keys/.
export type AiProvider = 'gemini' | 'openai';

// Who may use a stored key. 'user' (the default we create) means only its owner;
// 'team' / 'corporate' keys are provisioned by an admin and simply show up in
// the list for everyone entitled to them.
export type AiAccessScope = 'corporate' | 'team' | 'user';

export type AiApiKey = {
  id: number;
  provider: AiProvider;
  name: string | null;
  access_scope: AiAccessScope;
  monthly_limit_usd: number;
  default_user_limit: number | null;
  // Budget left on the key itself this month.
  remaining_usd: number;
  // Budget left for *this* user against the key (per-user sub-limit).
  user_remaining_usd: number;
  // Technical model names this key can serve — the source of truth for the model
  // picker. The frontend MODEL_CATALOG (model.ts) only supplies display names.
  models: string[];
};

export type CreateAiApiKeyPayload = {
  provider: AiProvider;
  // The raw secret. This is the only request that ever carries it.
  api_key: string; // minLength 8
  // Optional per the API schema, but a key stored without a name makes the
  // backend answer 500 later, so it is required here — non-empty, and unique per
  // owner (the DB constraint is on (name, owner), a repeat answers 409).
  name: string;
  // monthly_limit_usd is deliberately absent: the backend assigns the default
  // limit on creation, and only admins change it (per key, via Django Admin).
  default_user_limit?: number | null;
  access_scope?: AiAccessScope; // backend default: 'user'
  user_ids?: number[];
  team_ids?: number[];
};

// ─── Generation ──────────────────────────────────────────────────────
// A reference image. Exactly one of the two fields — sending both is a 422
// ("ровно одно из двух полей"), so the union is mutually exclusive at compile
// time. `asset_id` lets the backend fetch the bytes itself (no upload from the
// browser); `image_base64` is for uploads / pastes / non-raster originals.
//
// `image_base64` takes bare base64 *or* a full data URL, and the resolver sends
// the data URL so the reference's format travels with its bytes — see
// toInlineBase64 in imageResolver.ts for why that matters.
export type AiReference =
  | { asset_id: number; image_base64?: never }
  | { asset_id?: never; image_base64: string };

// Target output size for Gemini — the backend's `AiImageResolution` enum, which
// it forwards as `imageConfig.imageSize`. Which of the four values a given model
// accepts differs per model ("supported values depend on the model"), so the
// studio only offers the ones it knows about — see MODEL_CATALOG in model.ts,
// the single source of truth for that mapping (and of the `Resolution` type,
// which is checked against this union).
export type AiImageResolution = '512' | '1K' | '2K' | '4K';

export type AiGenerationPayload = {
  // Technical model name ('gpt-image-1', 'gemini-2.5-flash-image'), validated by
  // the backend against its price table for the key's provider.
  model: string;
  prompt: string; // minLength 4
  api_key_id: number;
  references?: AiReference[]; // max 8
  images_count?: number | null; // 1..10, OpenAI only
  size?: string | null; // OpenAI only
  quality?: string | null; // OpenAI only
  aspect_ratio?: string | null; // Gemini only (imageConfig.aspectRatio)
  resolution?: AiImageResolution | null; // Gemini only (imageConfig.imageSize)
};

export type AiGeneratedImage = {
  // Raw base64, without the "data:<mime>;base64," prefix.
  data: string;
  mime_type: string;
};

export type AiUsage = {
  prompt_tokens: number;
  output_tokens: number;
  total_tokens: number;
  cost_usd: number;
};

export type AiGenerationResponse = {
  // The backend can return several images (images_count); v1 shows images[0].
  images: AiGeneratedImage[];
  usage: AiUsage;
};

// ─── Usage report ────────────────────────────────────────────────────
// GET /usage/ — monthly spend. Not surfaced in v1 (the key list already carries
// remaining_usd / user_remaining_usd); kept so the future spend widget has a
// typed entry point.
export type AiUsageReport = {
  spent_usd: number;
  limit_usd: number;
  remaining_usd: number;
};

// ─── Requests ────────────────────────────────────────────────────────
export const loadAiApiKeys = (signal?: AbortSignal) =>
  safe.get<AiApiKey[]>({ url: `${AI_API}/api-keys/`, signal });

export const createAiApiKey = (payload: CreateAiApiKeyPayload) =>
  safe.post<AiApiKey>({ url: `${AI_API}/api-keys/`, payload });

export const deleteAiApiKey = (id: number) =>
  safe.delete<unknown>({ url: `${AI_API}/api-keys/${id}/` });

export const generateAi = (payload: AiGenerationPayload, signal?: AbortSignal) =>
  safe.post<AiGenerationResponse>({ url: `${AI_API}/generations/`, payload, signal });

export const loadAiUsage = () => safe.get<AiUsageReport>({ url: `${AI_API}/usage/` });
